import React from 'react';
import {
  PharmacistLegendVariant,
  AdminLegendVariant,
  PermissionsOfAdmin,
  PermissionsRemovalKeys,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import { renderScreen } from '../../actions/drawerActions';
import { ScreenTypes } from '../../helpers/Constants';
import strings from '../../localization';

import { useAppDispatch } from '../useAppDispatch';
import { serverToday, timeString } from '../../helpers/Functions';
import useWorkshiftDelete from '../Admin/useWorkshiftDelete';
import useAdminPermissions from '../Admin/useAdminPermissions';
import { removeBasedOnPermissions } from '../../helpers/permissionsHelper';
import { getDayMonthDateYear } from '../../components/Reports/helpers';
import { useAppSelector } from '../useAppSelector';
import { setDialog } from '../../reducers/dialogReducer';
import AdminLongTermShiftDetails from '../../components/Admin/AvailableWorkshifts/AdminLongTermShiftDetails';

const viewShiftKey = 'viewShift';

export type genericFunction = () => void;
export type IWorkshiftId = { workshiftId: string };
type IViewDetails = {
  workshiftId: string;
  date: string;
  pharmacyId: string;
  isAutoSyncRecord?: boolean;
};

type IWatchlistViewDetails = {
  workshiftId: string;
  pharmacyId: string;
  pharmacistId: string;
  isRequested: boolean;
};

export const menuActions = (
  editAction: genericFunction,
  viewDetailsAction: genericFunction,
  deleteWorkshiftAction: genericFunction,
  matchingAvailabilitiesAction: genericFunction,
  expressBookingAction: genericFunction,
  viewMultiShiftDetails: genericFunction,
) =>
  [
    {
      key: '1',
      label: strings.viewDetails,
      onClick: viewDetailsAction,
    },
    {
      key: viewShiftKey,
      label: strings.viewShiftDetails,
      onClick: viewMultiShiftDetails,
    },
    {
      key: '2',
      label: strings.edit,
      onClick: editAction,
    },
    {
      key: PermissionsRemovalKeys.Delete,
      label: strings.delete,
      onClick: deleteWorkshiftAction,
    },
    {
      key: '4',
      label: strings.matchingAvailabilities,
      onClick: matchingAvailabilitiesAction,
    },
    {
      key: PermissionsRemovalKeys.ExpressBooking,
      label: strings.expressBooking,
      onClick: expressBookingAction,
    },
  ];

const useAvailableWorkshiftActions = () => {
  const dispatch = useAppDispatch();

  const today = serverToday().toISO({ includeOffset: false }) ?? '';
  const timeFormat = useAppSelector(userPreferredTimeFormat);

  const { can } = useAdminPermissions();

  const { showWorkshiftDelete } = useWorkshiftDelete();

  const goToEditWorkshift = ({ workshiftId }: IWorkshiftId) => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 2,
        eventId: workshiftId,
      }),
    );
  };

  const goToAvailableWorkshift = (date: string) =>
    () => {
      dispatch(
        renderScreen({
          screenNumber: 1,
          screenType: ScreenTypes.availableWorkshift,
          date,
        }),
      );
    };

  const goToViewDetails = ({
    workshiftId,
    date,
    pharmacyId,
    isAutoSyncRecord,
  }: IViewDetails) => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 3,
        eventId: workshiftId,
        back: goToAvailableWorkshift(date),
        pharmacyId,
        extraParameters: {
          isAutoSyncRecord,
        },
      }),
    );
  };

  const viewMultiShiftDetails = (workshiftId: string) => {
    dispatch(
      setDialog({
        Component: <AdminLongTermShiftDetails workshiftId={workshiftId} />,
        heading: {
          title: strings.shiftDetails,
        },
        showCloseButton: true,
        maxWidth: 'lg',
      }),
    );
  };
  const goToWatchlists = (pharmacistId: string) =>
    () => {
      dispatch(
        renderScreen({
          screenType: ScreenTypes.adminWatchlists,
          screenNumber: 1,
          pharmacistId,
        }),
      );
    };

  const watchlistViewDetails = ({
    workshiftId,
    pharmacyId,
    pharmacistId,
    isRequested,
  }: IWatchlistViewDetails) => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 3,
        eventId: workshiftId,
        type: isRequested
          ? AdminLegendVariant.requested
          : AdminLegendVariant.posted,
        back: goToWatchlists(pharmacistId),
        pharmacyId,
      }),
    );
  };

  const goToMatchingAvailabilities = ({ workshiftId }: IWorkshiftId) => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 4,
        eventId: workshiftId,
        type: PharmacistLegendVariant.workshift,
      }),
    );
  };

  const goToExpressBooking = ({ workshiftId }: IWorkshiftId) => {
    dispatch(
      renderScreen({
        screenType: ScreenTypes.availableWorkshift,
        screenNumber: 5,
        eventId: workshiftId,
        type: AdminLegendVariant.posted,
      }),
    );
  };

  const actions = ({
    id,
    startDate,
    endDate,
    pharmacyId,
    isAutoSyncRecord,
    isLongTerm,
    postedCount,
  }: {
    id: string;
    startDate: string;
    endDate: string;
    pharmacyId: string;
    isAutoSyncRecord?: boolean;
    isLongTerm: boolean;
    postedCount: number;
  }) => {
    const date = getDayMonthDateYear(startDate);
    const time = timeString(startDate, endDate, timeFormat);
    const dateTime = `${date} | ${time}`;

    let menu = menuActions(
      () =>
        goToEditWorkshift({ workshiftId: id }),
      () =>
        goToViewDetails({
          workshiftId: id,
          date: startDate,
          pharmacyId,
          isAutoSyncRecord,
        }),
      () =>
        showWorkshiftDelete(id, dateTime, { isLongTerm, postedCount }),
      () =>
        goToMatchingAvailabilities({ workshiftId: id }),
      () =>
        goToExpressBooking({ workshiftId: id }),
      () =>
        viewMultiShiftDetails(id),
    );

    if (date < today) {
      menu = menu.slice(0, 1);
    }

    menu = removeBasedOnPermissions(
      menu,
      [PermissionsOfAdmin.Booking, PermissionsOfAdmin.Delete],
      can,
    );

    if (!isLongTerm) {
      menu = menu.filter((item) =>
        item.key !== viewShiftKey);
    }

    return menu;
  };

  return {
    actions,
    goToViewDetails,
    watchlistViewDetails,
  };
};

export default useAvailableWorkshiftActions;
